import { cexApi } from './apiBase.js'
import { apiFetch } from './apiClient.js'

/**
 * CEX request failed; `body` is parsed JSON when possible (`detail` = machine codes from
 * the control-plane, e.g. `exchange-api-auth`).
 */
export class CexHttpError extends Error {
  /**
   * @param {string} path
   * @param {number} status
   * @param {unknown} body
   * @param {string} rawText
   */
  constructor(path, status, body, rawText) {
    super(
      body && typeof body === 'object' && 'detail' in body
        ? `cex ${path} ${status}: ${String(body.detail)}`
        : `cex ${path} ${status}: ${rawText.slice(0, 200)}${rawText.length > 200 ? '…' : ''}`,
    )
    this.name = 'CexHttpError'
    this.path = path
    this.status = status
    this.body = body
    this.rawText = rawText
  }
}

function _isHtmlResponse(text) {
  if (typeof text !== 'string' || !text) return false
  const t = text.trimStart()
  return /<!DOCTYPE/i.test(t) || /^<html/i.test(t)
}

/**
 * Control-plane / proxy unreachable, HTML error page, or CDN edge error — not a JSON
 * CEX business error. Inline widgets should stay quiet; the shell shows CEX health.
 */
export function isCexAvailabilityError(err) {
  if (!err || typeof err !== 'object') return false
  if (err.name === 'AbortError') return false
  if (err.name === 'CexHttpError') {
    const s = err.status
    if (s === 521 || s === 522 || s === 523 || s === 524) return true
    const b = err.body
    if (b && typeof b === 'object' && 'detail' in b && b.detail != null) return false
    if (s === 502 || s === 503 || s === 504) return true
    if (b && typeof b === 'object' && b.raw && _isHtmlResponse(String(b.raw)) && s >= 400) {
      return true
    }
    const raw = typeof err.rawText === 'string' ? err.rawText : ''
    if (_isHtmlResponse(raw) && s >= 400) return true
    if (s === 404) {
      return true
    }
    if (s >= 500 && s < 600) {
      return true
    }
    return false
  }
  const m = err.message != null ? String(err.message) : ''
  if (m === 'Failed to fetch' || m === 'Load failed') return true
  if (m === 'NetworkError when attempting to fetch resource.') return true
  if (m.includes('Failed to fetch') || m.includes('Load failed')) return true
  if (m.includes('NetworkError when attempting to fetch resource')) return true
  if (m === 'The Internet connection appears to be offline.') return true
  if (m === 'The network connection was lost.') return true
  return false
}

/** For chart / order book / balances: hide transport noise; still show real API `detail` errors. */
export function formatCexInlineDataError(err) {
  if (isCexAvailabilityError(err)) return ''
  return formatCexUserError(err)
}

/**
 * DEX/Groups or other fetches: suppress top banner for generic network/edge failures
 * (same user-visible cases as CEX, minus `CexHttpError` — those only come from cexJson).
 */
export function isPlatformAvailabilityFailure(err) {
  if (isCexAvailabilityError(err)) return true
  if (!err || typeof err !== 'object') return false
  const m = err.message != null ? String(err.message) : ''
  if (m === 'Failed to fetch' || m === 'Load failed') return true
  if (m === 'NetworkError when attempting to fetch resource.') return true
  if (m.includes('Failed to fetch') || m.includes('Load failed')) return true
  if (m.includes('NetworkError when attempting to fetch resource')) return true
  if (m === 'The Internet connection appears to be offline.') return true
  if (m === 'The network connection was lost.') return true
  return false
}

/** Session not confirmed on CEX: Redis + DEX. Also used when only `err.message` contains the code. */
const AUTH_SERVICE_UNREACHABLE =
  'CEX session not confirmed: no Redis key and DEX is unreachable. ' +
  'Use the same REDIS_URL on LTDashboard and control-plane, sign out and back in, ' +
  'and check LTDASHBOARD_BASE_URL (CEX can reach DEX on the network).'

/** Human-readable one-liner; avoids dumping raw exchange URLs. */
export function formatCexUserError(err) {
  const fromMsg = err && typeof err === 'object' && err.message ? String(err.message) : ''
  if (fromMsg.includes('auth-service-unreachable')) {
    return AUTH_SERVICE_UNREACHABLE
  }
  if (err && typeof err === 'object' && err.name === 'CexHttpError' && err.body) {
    const d = err.body && typeof err.body === 'object' && err.body.detail
    const code = typeof d === 'string' ? d : null
    if (code === 'exchange-api-auth') {
      return 'Exchange rejected this API key. Confirm key/secret, enable read (and trade if needed), and match spot vs futures. IP whitelist is optional; only if you enable it, add the server’s public IP.'
    }
    if (code === 'exchange-permission-denied') {
      return 'Exchange denied permission (API scope, IP, or account state).'
    }
    if (code === 'mexc-ip-not-whitelisted') {
      return (
        'MEXC: this API key is IP-restricted and the CEX server outbound IP is not on the list. ' +
        'In MEXC → API Management, add the control-plane public outbound IP to the key whitelist, or turn off IP restriction for that key.'
      )
    }
    if (code === 'mexc-symbol-not-enabled-for-api') {
      return (
        'MEXC (10007, symbol not support api): this API key often does not have trading allowed for this pair. ' +
        'Open the key used by CEX in API Management and allow this symbol for trading (or allow all, if available). ' +
        'Chart/depth use public APIs and can work while private orders fail. ' +
        'Check spot (not futures) and Trade permission.'
      )
    }
    if (code === 'rate-limit') {
      return 'Exchange rate limit — try again in a few seconds.'
    }
    if (code === 'exchange-circuit-open') {
      return (
        'The exchange API is briefly short-circuited after repeated failures (circuit open). ' +
        'Wait a few seconds and retry.'
      )
    }
    if (typeof d === 'string' && d.startsWith('invalid-order:')) {
      const rest = d.slice('invalid-order:'.length).trim()
      if (rest) {
        return `Order rejected: ${rest}`
      }
      return 'Order rejected: check price tick, amount lot size, and min notional on the exchange.'
    }
    if (typeof d === 'string' && (d === 'bad-symbol' || d.startsWith('bad-symbol:'))) {
      const venue = d.startsWith('bad-symbol:') ? d.slice('bad-symbol:'.length).trim() : ''
      const combined = `${venue} ${fromMsg}`.toLowerCase()
      // MEXC 10007: key-level symbol allowlist is a common cause — public chart/depth ignore it.
      if (
        combined.includes('10007') &&
        (combined.includes('not support api') || combined.includes('symbol not support'))
      ) {
        return (
          'MEXC (10007, symbol not support api): this API key often does not have trading allowed for this pair. ' +
          'Open the key used by CEX in API Management and allow this symbol for trading (or allow all, if available). ' +
          'Chart/depth use public APIs and can work while private orders fail. ' +
          'Check spot (not futures) and Trade permission. ' +
          (venue ? `Venue: ${venue}` : '')
        )
      }
      const base =
        'This symbol is not available on the exchange for spot (wrong format, wrong market, or not listed). ' +
        'Use BASE/QUOTE as the venue expects (e.g. BTC/USDT).'
      return venue ? `${base} — ${venue}` : base
    }
    if (code === 'insufficient-balance') {
      return (
        'Not enough free balance on this API account for all grid limit orders (estimate vs quote range and order book).'
      )
    }
    if (code === 'grid-restart-insufficient-balance') {
      return (
        'Restart aborted: not enough balance for the new grid from config — existing limit orders were not cancelled. ' +
        'Add funds or reduce the grid, save, and retry.'
      )
    }
    if (code === 'grid-restart-preflight-failed') {
      return (
        'Restart aborted: could not verify order book or balance — existing orders were not cancelled. Retry later or check the API key.'
      )
    }
    if (code === 'handover-lock-busy') {
      return (
        'Restart is already in progress for this bot (concurrent request or background work). Try again in a few seconds.'
      )
    }
    if (code === 'account-trading-halted' || d === 'account-trading-halted') {
      return (
        'Trading on this API account is halted. Clear the halt in control-plane, then start bots.'
      )
    }
    if (
      typeof d === 'string' &&
      (d.includes('grid-config:granularity-too-fine') || d.includes('granularity-too-fine-for-tick'))
    ) {
      return (
        'Grid is too fine for the instrument tick size — widen step/size or set tickSize in config.'
      )
    }
    if (
      code === 'invalid-nonce' ||
      (typeof d === 'string' && d.startsWith('invalid-nonce'))
    ) {
      return (
        'Exchange rejected the request (time/nonce out of window). Sync the host that runs CEX control-plane with NTP (chrony), ' +
        'not your laptop. If this persists, check CEX_CCXT_TIME_RESYNC_SEC and server clock stability (Binance/MEXC/…).'
      )
    }
    if (code === 'auth-service-unreachable') {
      return AUTH_SERVICE_UNREACHABLE
    }
    if (
      code === 'missing-flag:can_access_cex_tokens' ||
      (typeof d === 'string' && d.includes('can_access_cex_tokens'))
    ) {
      return (
        'CEX: your profile is missing access to token groups. Update LTDashboard, sign out and back in, ' +
        'or ask an admin to enable CEX / token groups for your user in Settings.'
      )
    }
    if (
      typeof d === 'string' &&
      /^exchange-(?:unavailable|dns-failed|proxy-failed|timeout|tls-failed|conn-reset):/.test(d)
    ) {
      return 'Could not reach the exchange (network, timeout, or venue unavailable). If every request shows 502 with no JSON, check the proxy to the control-plane server instead.'
    }
    if (typeof d === 'string' && (d.includes('capital/config') || d.includes('api.mexc.com'))) {
      return 'MEXC rejected a signed request. Check key/secret, API permissions (read, and trade for orders), and that the key matches spot or futures. IP restriction is not required on MEXC; if you turn it on, add the control-plane server’s public IP.'
    }
    if (code != null) return String(code)
  }
  const m = err && err.message
  if (typeof m === 'string') {
    if (m.length > 240) return `${m.slice(0, 120)}…`
    return m
  }
  return 'Request failed'
}

/** POST /api/cex/bots returned 400 insufficient-balance (grid preflight). */
export function isCexInsufficientBalance(err) {
  if (!err || typeof err !== 'object' || err.name !== 'CexHttpError') return false
  const d = err.body && typeof err.body === 'object' ? err.body.detail : null
  if (d === 'insufficient-balance' || d === 'grid-restart-insufficient-balance') return true
  const msg = typeof err.message === 'string' ? err.message : ''
  return msg.includes('insufficient-balance')
}

/**
 * Optional toast duration for CEX business errors (e.g. 5s for grid balance hints).
 * @param {unknown} err
 * @returns {number | undefined}
 */
export function cexErrorToastDurationMs(err) {
  if (!err || typeof err !== 'object' || err.name !== 'CexHttpError') return undefined
  const d = err.body && typeof err.body === 'object' && 'detail' in err.body ? err.body.detail : null
  if (d === 'grid-restart-insufficient-balance' || d === 'insufficient-balance') return 5_000
  return undefined
}

/** All CEX (cexterminal) API calls go through this module. */
export async function cexFetch(path, init = {}) {
  const url = cexApi(path)
  const res = await apiFetch(url, {
    credentials: 'include',
    cache: init.cache ?? 'no-store',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  })
  return res
}

export async function cexJson(path, init = {}) {
  const res = await cexFetch(path, init)
  const text = await res.text().catch(() => '')
  if (!res.ok) {
    let body = text
    try {
      body = text ? JSON.parse(text) : {}
    } catch {
      body = { raw: text }
    }
    throw new CexHttpError(path, res.status, body, text)
  }
  if (res.status === 204) return null
  if (!text) return null
  return JSON.parse(text)
}

function dashboardQuery(period = '7d', mode = 'rolling') {
  return new URLSearchParams({ period: period || '7d', mode: mode || 'rolling' }).toString()
}

export function dashboardSnapshotPath(groupId, period = '7d', mode = 'rolling') {
  return `/api/cex/dashboard/${encodeURIComponent(groupId)}?${dashboardQuery(period, mode)}`
}

export function dashboardDepthHistoryPath(groupId, period = '7d', mode = 'rolling') {
  return `/api/cex/dashboard/${encodeURIComponent(groupId)}/depth_history?${dashboardQuery(period, mode)}`
}

export function dashboardVolumeHistoryPath(groupId, period = '7d', mode = 'rolling') {
  return `/api/cex/dashboard/${encodeURIComponent(groupId)}/volume_history?${dashboardQuery(period, mode)}`
}

export function getDashboardSnapshot(groupId, period = '7d', mode = 'rolling') {
  return cexJson(dashboardSnapshotPath(groupId, period, mode))
}

export function getDashboardDepthHistory(groupId, period = '7d', mode = 'rolling') {
  return cexJson(dashboardDepthHistoryPath(groupId, period, mode))
}

export function getDashboardVolumeHistory(groupId, period = '7d', mode = 'rolling') {
  return cexJson(dashboardVolumeHistoryPath(groupId, period, mode))
}

export function patchDashboardMeta(groupId, payload) {
  return cexJson(`/api/cex/token-groups/${encodeURIComponent(groupId)}/meta`, {
    method: 'PATCH',
    body: JSON.stringify(payload || {}),
  })
}

export function resetDashboardBaseline(groupId) {
  return cexJson(`/api/cex/token-groups/${encodeURIComponent(groupId)}/baseline/reset`, {
    method: 'POST',
    body: '{}',
  })
}
